import React, { useState } from 'react';
import { Alert, Button, InputNumber, Modal, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { cleanRpcError } from 'client/modules/common/utils/rpcError';
import useSetTeamAttribute from '../../hooks/useSetTeamAttribute';
import {
  NOT_SET_LABEL,
  isStorableNumericAttribute,
  parseNumericAttribute,
} from '../../utils/mcpAttributes';

type Props = {
  idTeam: number;
  attribute: string;
  label: string;
  type: string;
  value: string | null;
  refetch: () => void;
  disabled?: boolean;
};

const EditMcpNumber = ({
  idTeam,
  attribute,
  label,
  type,
  value,
  refetch,
  disabled,
}: Props) => {
  const currentValue = parseNumericAttribute(value);

  const [isModalVisible, setModalVisible] = useState(false);
  const [nextValue, setNextValue] = useState<number | null>(currentValue);
  const [saveError, setSaveError] = useState('');

  const { onSubmit, isLoading } = useSetTeamAttribute(idTeam);

  const isUnchanged = nextValue === currentValue;
  const isInvalid = !isStorableNumericAttribute(nextValue);

  const handleOpenModal = () => {
    setNextValue(currentValue);
    setSaveError('');
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setNextValue(currentValue);
    setSaveError('');
  };

  const handleSave = async () => {
    if (isInvalid) {
      return;
    }

    setSaveError('');

    try {
      await onSubmit({ attribute, type, value: String(nextValue) });
      message.success(`Success! ${label} has been updated.`);
      refetch();
      setModalVisible(false);
    } catch (error) {
      setSaveError(cleanRpcError(error));
    }
  };

  return (
    <>
      <span>{value === null || value === '' ? NOT_SET_LABEL : value}</span>
      <div className="pr-2">
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={handleOpenModal}
          disabled={disabled}
          aria-label={`Edit ${label}`}
        />
      </div>

      <Modal
        title={`Edit ${label}`}
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{
          disabled: isUnchanged || isInvalid || isLoading,
          loading: isLoading,
        }}
        cancelButtonProps={{ disabled: isLoading }}
      >
        <InputNumber
          className="w-full"
          min={0}
          precision={0}
          value={nextValue}
          onChange={(newValue) => {
            setSaveError('');
            const parsed = newValue === null ? null : Number(newValue);
            setNextValue(
              parsed !== null && Number.isFinite(parsed) ? parsed : null
            );
          }}
          placeholder={`Enter a value for ${attribute}`}
          aria-label={label}
        />

        {!!saveError && (
          <Alert className="mt-4" type="error" showIcon message={saveError} />
        )}
      </Modal>
    </>
  );
};

export default EditMcpNumber;
